// @file Whiteboard toolbar store, mainly for the visuals of secondary menu and tooltips.
import device from '@@/bits/device'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import type { WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useWhiteboardToolbarStore = defineStore('whiteboardToolbar', () => {
  // Secondary Menu
  // 8 (distance between toolbar icon and toolbar edge) + 4 (distance between toolbar edge and menu)
  const GEO_SHAPE_OR_COLOR_MENU_OFFSET = 8 + 4

  const whiteboardStore = useWhiteboardStore()
  const selectedToolPosition = computed(() => {
    for (const section of whiteboardStore.toolSections) {
      for (const [index, toolId] of section.entries()) {
        if (toolId === whiteboardStore.selectedToolId) return index
      }
    }
    return -1
  })

  const secondaryMenuMainAxisOffset = computed(() => {
    if (selectedToolPosition.value % 2 === 1) {
      return GEO_SHAPE_OR_COLOR_MENU_OFFSET + 44
    }
    return GEO_SHAPE_OR_COLOR_MENU_OFFSET
  })

  const geoColorMenuOffset = computed(() => {
    return secondaryMenuMainAxisOffset.value + 40 + 4
  })

  const secondaryMenuCrossAxisOffset = computed(() => {
    if (whiteboardStore.selectedToolId === 'draw-nested') {
      const numberOfColorItems = whiteboardStore.selectedSecondaryTool?.secondaryMenu?.items.length ?? 0
      const ICON_HEIGHT = device.touchable ? 36 : 24
      const ICON_MARGIN = 4
      const ICON_LIST_PADDING = 8
      if (numberOfColorItems === 0) {
        return 0
      }
      return (ICON_HEIGHT * numberOfColorItems + ICON_MARGIN * (numberOfColorItems - 1)) / 2 + ICON_LIST_PADDING
    }
    return 0
  })

  // Secondary Menu logic

  function getNestedColorMenu(): WhiteboardSecondaryMenu | null {
    if (whiteboardStore.selectedTool == null) return null
    // Get the current secondary selection ID for the selected tool
    const currentSecondarySelectionId =
      whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId

    // Find the corresponding menu item in the secondary menu items
    const selectedItem = whiteboardStore.selectedTool.secondaryMenu?.items.find(
      (item) => item.id === currentSecondarySelectionId,
    )

    // Extract the nested color menu from the selected item, if it exists
    const nestedColorMenu = selectedItem?.secondaryMenu ?? null
    return nestedColorMenu
  }

  function getNestedSelectedColorId(): string | null {
    if (whiteboardStore.selectedTool == null) return null
    // Get the current secondary selection ID for the selected tool
    const currentSecondarySelectionId =
      whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId

    // Find the corresponding menu item in the secondary menu items
    const selectedItem = whiteboardStore.selectedTool.secondaryMenu?.items.find(
      (item) => item.id === currentSecondarySelectionId,
    ) as WhiteboardTool
    // Extract the nested color menu from the selected item, if it exists
    const selectedColorId = whiteboardStore.toolStates[selectedItem?.id]?.currentSecondarySelectionId ?? null
    return selectedColorId
  }

  function getFillStyleMenu(): WhiteboardSecondaryMenu | null {
    const fillStyleMenu = {
      id: 'fill-style',
      items: [
        { label: 'None', iconName: 'fill-none', id: 'none', tldrawToolId: 'none' },
        { label: 'White', iconName: 'fill-white', id: 'semi', tldrawToolId: 'semi' },
        { label: 'Solid', iconName: 'fill-solid', id: 'solid', tldrawToolId: 'solid' },
      ],
      defaultSelection: 'semi',
    }
    return fillStyleMenu
  }

  function getFillStyleMenuSelectionId(): string {
    return whiteboardStore.toolStates.geo?.fillStyle ?? 'semi'
  }

  const currentSecondaryMenus = computed<WhiteboardSecondaryMenu[]>(() => {
    // Return an empty array if no tool is selected
    if (whiteboardStore.selectedTool == null) return []

    // Initialize the secondary menus array with the selected tool's secondary menu
    let secondaryMenus = [whiteboardStore.selectedTool.secondaryMenu] as WhiteboardSecondaryMenu[]

    // Add the geoColorMenu if the selected tool is 'geo'
    if (whiteboardStore.selectedTool.id === 'geo' && whiteboardStore.geoColorMenu != null) {
      const fillStyleMenu = getFillStyleMenu()
      if (fillStyleMenu != null) {
        secondaryMenus = secondaryMenus.concat(fillStyleMenu)
      }
      secondaryMenus = secondaryMenus.concat(whiteboardStore.geoColorMenu).reverse()
    }

    if (whiteboardStore.selectedTool?.id === 'draw-nested') {
      const nestedColorMenu = getNestedColorMenu()

      // Add the nested color menu to the secondary menus array
      if (nestedColorMenu != null) {
        secondaryMenus = secondaryMenus.concat(nestedColorMenu)
      }
    }
    // Filter out any null menus from the array
    secondaryMenus = secondaryMenus.filter((menu) => menu != null)

    return secondaryMenus
  })

  const currentSecondarySelectionIds = computed<string[]>(() => {
    let selectionIds = [whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId]
    if (whiteboardStore.selectedTool?.id === 'geo' && whiteboardStore.geoColorMenu != null) {
      const fillStyleMenuSelectionId = getFillStyleMenuSelectionId()
      selectionIds = selectionIds.concat(fillStyleMenuSelectionId)
      selectionIds = selectionIds.concat(whiteboardStore.geoColorMenuSelectionId).reverse()
    }
    if (whiteboardStore.selectedTool?.id === 'draw-nested') {
      const selectedColorId = getNestedSelectedColorId()

      // Add the selected nested color menu to the secondary menus
      if (selectedColorId != null) selectionIds = selectionIds.concat(selectedColorId)
    }

    selectionIds = selectionIds.filter((id) => id != null)
    return selectionIds as string[]
  })

  function handleSecondaryMenuSelection(item, secondaryMenuId): void {
    if (
      whiteboardStore.selectedTool?.id === 'geo' &&
      whiteboardStore.geoColorMenu != null &&
      secondaryMenuId === 'color'
    ) {
      whiteboardStore.geoColorMenuSelectionId = item.id
      return
    }

    if (whiteboardStore.selectedTool?.id === 'geo' && secondaryMenuId === 'fill-style') {
      whiteboardStore.toolStates = {
        ...whiteboardStore.toolStates,
        geo: {
          ...whiteboardStore.toolStates.geo,
          fillStyle: item.id,
        },
      }
      return
    }

    if (whiteboardStore.selectedTool?.id === 'draw-nested' && secondaryMenuId === 'color') {
      if (whiteboardStore.selectedSecondaryTool == null) return
      whiteboardStore.toolStates = {
        ...whiteboardStore.toolStates,
        [whiteboardStore.selectedSecondaryTool?.id]: {
          ...whiteboardStore.toolStates[whiteboardStore.selectedSecondaryTool?.id],
          currentSecondarySelectionId: item.id,
        },
      }
      return
    }

    whiteboardStore.toolStates = {
      ...whiteboardStore.toolStates,
      [whiteboardStore.selectedToolId]: {
        ...whiteboardStore.toolStates[whiteboardStore.selectedToolId],
        currentSecondarySelectionId: item.id,
      },
    }
  }
  /** TOOLS' TOOLTIP
   * If the user hover on a tool for 2s, we start showing tooltip for any tools hovered on
   * until the user hover out of the toolbar for more than 1s.
   */

  // State
  const xTooltip = ref(false)
  const tooltipToolId = ref<string | null>(null)

  // Utilities
  const TOOLTIPS_ON_DELAY = 500
  const TOOLTIPS_OFF_DELAY = 1000
  let tooltipShowingTimeout: ReturnType<typeof setTimeout> | null = null
  let tooltipHidingTimeout: ReturnType<typeof setTimeout> | null = null
  function clearTooltipShowingTimeout(): void {
    if (tooltipShowingTimeout != null) {
      clearTimeout(tooltipShowingTimeout)
      tooltipShowingTimeout = null
    }
  }
  function clearTooltipHidingTimeout(): void {
    if (tooltipHidingTimeout != null) {
      clearTimeout(tooltipHidingTimeout)
      tooltipHidingTimeout = null
    }
  }

  // Actions
  function trackToolHoverOn(toolId: string): void {
    tooltipToolId.value = toolId
    clearTooltipShowingTimeout()
    clearTooltipHidingTimeout()
    tooltipShowingTimeout = setTimeout(() => {
      xTooltip.value = true
      clearTooltipShowingTimeout()
    }, TOOLTIPS_ON_DELAY)
  }

  function trackToolHoverOff(): void {
    tooltipToolId.value = null
    clearTooltipShowingTimeout()
    tooltipHidingTimeout = setTimeout(() => {
      xTooltip.value = false
      clearTooltipHidingTimeout()
    }, TOOLTIPS_OFF_DELAY)
  }

  const hoveredToolPosition = computed(() => {
    for (const section of whiteboardStore.toolSections) {
      for (const [index, toolId] of section.entries()) {
        if (toolId === tooltipToolId.value) return index
      }
    }
    return -1
  })

  // 8 (distance from tooltip icon to toolbar edge) + 4 (distance from toolbar edge to tooltip)
  const TOOLTIP_OFFSET = 8 + 4

  const tooltipOffset = computed(() => {
    if (whiteboardStore.isLoadingPortraitMobileLayout) {
      return TOOLTIP_OFFSET
    }
    if (hoveredToolPosition.value % 2 === 1) {
      return TOOLTIP_OFFSET + 44
    }
    return TOOLTIP_OFFSET
  })
  return {
    currentSecondaryMenus,
    currentSecondarySelectionIds,
    secondaryMenuMainAxisOffset,
    secondaryMenuCrossAxisOffset,
    geoColorMenuOffset,
    tooltipOffset, // State
    xTooltip,
    tooltipToolId,

    // Actions
    trackToolHoverOn,
    trackToolHoverOff,
    handleSecondaryMenuSelection,
  }
})
